import { useState, useEffect } from "react";
import axios from "axios";
import { Button, OutlineButton } from "components/button";
import { useModalContext } from "components/modal";
import { useSnackbarContext } from "components/snackbar";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { TProperty } from "types/property";
import { useAreYouSure } from "components/areYouSure";
import { FaInfoCircle } from "react-icons/fa";
import TooltippedElement from "components/TooltippedElement";
import CustomDocModal from "containers/customDocModal";
import Field from "components/field";
import { useTranslation } from 'react-i18next';
import MultiInput from "components/input/multi";

type Props = {
  emails: string[] | null;
  invoiceId: string;
  isReceipt?: boolean;
};

export default function GenerateInvoicePDFModal(props: Props) {
  const { t } = useTranslation();
  const tBase = "views.invoice.GenerateInvoicePDFModal";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { isReceipt } = props;

  const queryClient = useQueryClient();
  const modal = useModalContext();
  const { showSnackbar } = useSnackbarContext();

  const [templateId, setTemplateId] = useState<string>();
  const [emails, setEmails] = useState<(string | null)[] | null>();
  const [billing, setBilling] = useState<TProperty>();
  const [, setMailOnly] = useState<boolean>(false);
  const [completion_notes, setCompletionNotes] = useState("");
  const [includeHeaders, setIncludeHeaders] = useState(true);
  const [noColor, setNoColor] = useState(false);
  const [showReceiptMessage, setShowReceiptMessage] = useState(true);

  const getSendDetailsQuery = useQuery(
    ["invoice-send-details"],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/send-details`
        )
        .then((res) => res.data),
    {
      refetchOnWindowFocus: false,
      onSuccess: (details) => {
        if (details) {
          setBilling(details.sendDetails[0].billing_address);
          setTemplateId(details.sendDetails[0].fk_customdoc_id);
          setCompletionNotes(details.sendDetails[0].completion_notes);
          if (details.mailOnly) {
            setMailOnly(details.mailOnly);
          }
        }
      },
    }
  );

  const billingAddress = useQuery<TProperty[] | null>(
    ["billing address"],
    async () =>
      await axios
        .get(
          `
  ${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/billing_address
  `
        )
        .then((res) => res.data)
  );

  const preview = useMutation(
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/preview`,
          {
            params: {
              templateId,
              completion_notes,
              billing,
              includeHeaders,
              noColor,
            },
          }
        )
        .then((res) => res.data),
    {
      onSuccess: (url) => {
        window.open(url, "_blank");
        queryClient.invalidateQueries();
        modal.setModal();
      },
    }
  );

  const sendInvoiceReceipt = useMutation(
    async (emails: string[]) => await axios
      .post(`${process.env.REACT_APP_SERVER_URL}/api/v1/company/invoice/${props.invoiceId}/send-receipt`, {
        emails,
        templateId,
        completion_notes,
        billing,
        includeHeaders,
        noColor,
      })
      .then((res) => res.data),
    {
      onSuccess: () => {
        modal.setModal();
        queryClient.invalidateQueries();
        showSnackbar(tr("Invoice receipt sent!"));
      },
    }
  );

  const handlePreview = () => {
    preview.mutate();
  };

  const handleSendReceipt = () => {
    if (!emails || emails.length === 0) {
      showSnackbar(tr("Please enter at least one email address."));
      return;
    }
    sendInvoiceReceipt.mutate(emails.filter((email): email is string => email !== null));
  };


  
  useEffect(() => {
    if (props.emails) {
      setEmails(props.emails);
    }
  }, [props.emails]);

  const populateBillingAddresses = billingAddress.data?.map((address) => {
    return (
      <label key={address.id} className="flex items-center space-x-2">
        <input
          type="radio"
          value={address.id}
          name="billing_address"
          onChange={(e: any) => {
            setBilling(address);
          }}
          checked={billing?.id === address.id}
        />
        <span> {address.full_address}</span>
      </label>
    );
  });

  useEffect(() => {
    if (templateId || completion_notes || billing) {
      setShowReceiptMessage(false);
    }
  }, [templateId, completion_notes, billing]);

  return (
    <div className="max-w-[500px] space-y-4">
      {isReceipt && showReceiptMessage && (
        <div className="text-red-500">
          {tr("A PDF was not found on file. Please select which template and billing address you would like included in the receipt.")}
        </div>
      )}
      {isReceipt && (
        <MultiInput values={emails} onChange={(e) => setEmails(e)} />
      )}
      <CustomDocModal
        id={templateId}
        type="invoice"
        onClick={(id) => setTemplateId((tId) => (tId === id ? undefined : id))}
      />
      <div>
        <h3>{tr("Billing Address")}</h3>
        <div className="flex flex-col">{populateBillingAddresses}</div>
      </div>
      <div>
        <h3>{tr("Completion Notes")}</h3>
        <div className="flex flex-col">
          <Field
            type="textarea"
            value={completion_notes}
            onChange={(e) => setCompletionNotes(e.target.value)}
            minRows={5}
          />
        </div>
      </div>

      <div className="flex items-center">
        <input 
          type="checkbox" 
          checked={includeHeaders} 
          onChange={() => setIncludeHeaders(!includeHeaders)} 
        />
        <label className="ml-2">{tr("Include Headers")}</label>
        <TooltippedElement 
          message={tr("Turn On/Off Headers for Mail Only Customers")}
          placement="top"
          element={
            <FaInfoCircle className="ml-1 text-secondary" />
          }
        />
      </div>
      <div className="flex items-center [&>*]:-mt-1.5 [&>*]:-mb-2.5">
        <input 
          type="checkbox" 
          checked={noColor} 
          onChange={() => setNoColor(!noColor)} 
        />
        <label className="ml-2">{tr("Black & White Print")}</label>
        <TooltippedElement 
          message={tr("Turn Off Color for Printing PDF")}
          placement="top"
          element={
            <FaInfoCircle className="ml-1 text-secondary" />
          }
        />
      </div>
      <div className="flex justify-end space-x-2">
        {isReceipt ? (
          <Button
            primary
            type="submit"
            isLoading={sendInvoiceReceipt.isLoading}
            disabled={!templateId || !billing}
            onClick={handleSendReceipt}
          >
            {tr("Send Receipt")}
          </Button>
        ) : (
          <Button
            primary
            type="submit"
            isLoading={preview.isLoading}
            disabled={!templateId || !billing}
            onClick={handlePreview}
          >
            {tr("View PDF")}
          </Button>
        )}

        <OutlineButton onClick={() => modal.setModal()}>{tr("Cancel")}</OutlineButton>
      </div>
    </div>
  );
}
